import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Building2, Check, ChevronDown, Plus, Send } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import api from '../../services/api';
import Button from './Button';
import Alert from './Alert';
import { getFirstAccessibleRoute } from '../../lib/navigationUtils';

interface OrganizationSwitcherProps {
  onSwitch?: (orgId: string) => void;
}

interface AvailableOrganization {
  id: string;
  name: string;
  description?: string;
}

export default function OrganizationSwitcher({ onSwitch }: OrganizationSwitcherProps) {
  const {
    currentOrganizationId,
    organizations,
    permissions,
    setOrganizations,
    setCurrentOrganization,
    setPermissions,
    setOrganizationModules,
  } = useAuthStore();
  
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [availableOrgs, setAvailableOrgs] = useState<AvailableOrganization[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [requestMessage, setRequestMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [requestError, setRequestError] = useState('');
  const [requestSuccess, setRequestSuccess] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Check if user is platform admin (has wildcard permission)
  const isPlatformAdmin = useMemo(() => {
    return permissions?.includes('*.*') || false;
  }, [permissions]);

  useEffect(() => {
    // Refetch organizations when platform admin status changes or on mount
    fetchOrganizations();
  }, [isPlatformAdmin]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      
      // Platform admins get all organizations, regular users get only their memberships
      const endpoint = isPlatformAdmin ? '/organizations' : '/memberships/me';
      const response = await api.get(endpoint);
      const orgs = response.data.data || [];
      
      // For platform admins, transform the organization data to match membership format.
      // enabled_modules is preserved so the org-switcher can apply it without a
      // second API call when the admin switches context.
      const transformedOrgs = isPlatformAdmin
        ? orgs.map((org: any) => ({
            id: org.id,
            name: org.name,
            role: 'Platform Admin',
            enabled_modules: org.enabled_modules ?? ['mvp0_core', 'mvp0_enterprise'],
          }))
        : orgs;
      
      setOrganizations(transformedOrgs);
      
      // Set current organization if not set
      if (!currentOrganizationId && transformedOrgs.length > 0) {
        setCurrentOrganization(transformedOrgs[0].id);
      }
    } catch (err) {
      console.error('Failed to load organizations:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSwitch = async (orgId: string) => {
    if (orgId === currentOrganizationId) {
      setIsOpen(false);
      return;
    }

    try {
      setSwitching(true);
      
      // Platform admins can switch directly without API call
      // Regular users need to call the API to switch context
      if (isPlatformAdmin) {
        // Update auth store with new context
        setCurrentOrganization(orgId);
        
        // Platform admins keep their wildcard permissions
        setPermissions(['*.*']);

        // Resolve the target org's enabled_modules so the sidebar reflects its
        // vertical configuration.
        // Primary: the full org list was already fetched into state with enabled_modules
        //          included — reuse it without a second network call.
        // Fallback: fetch the org directly; if that also fails, use safe defaults.
        const cached = (organizations as any[]).find((o) => o.id === orgId);
        if (cached?.enabled_modules) {
          setOrganizationModules(cached.enabled_modules);
        } else {
          try {
            const res = await api.get(`/organizations/${orgId}`);
            const mods: string[] =
              res.data?.data?.enabled_modules ??
              res.data?.enabled_modules ??
              ['mvp0_core', 'mvp0_enterprise'];
            setOrganizationModules(mods);
          } catch {
            setOrganizationModules(['mvp0_core', 'mvp0_enterprise']);
          }
        }
        
        // Call optional callback
        if (onSwitch) {
          await onSwitch(orgId);
        }
        
        setIsOpen(false);
        
        // Redirect to first accessible route based on new permissions
        const redirectPath = getFirstAccessibleRoute(['*.*']);
        window.location.href = redirectPath;
      } else {
        // Regular users: call API to switch organization context
        const response = await api.post('/memberships/switch-context', {
          organization_id: orgId,
        });
        
        // Update auth store with new context
        setCurrentOrganization(orgId);
        
        // Update permissions and modules for new organization
        if (response.data.data?.permissions) {
          setPermissions(response.data.data.permissions);
        }
        if (response.data.data?.enabled_modules) {
          setOrganizationModules(response.data.data.enabled_modules);
        }
        
        // Call optional callback
        if (onSwitch) {
          await onSwitch(orgId);
        }
        
        setIsOpen(false);
        
        // Redirect to first accessible route based on new permissions
        const redirectPath = getFirstAccessibleRoute(response.data.data?.permissions || []);
        window.location.href = redirectPath;
      }
    } catch (err) {
      console.error('Failed to switch organization:', err);
      alert('Failed to switch organization. Please try again.');
    } finally {
      setSwitching(false);
    }
  };

  const fetchAvailableOrganizations = async () => {
    try {
      const response = await api.get('/organizations/public/list');
      const allOrgs = response.data.data || [];
      
      // Filter out organizations user is already a member of
      const memberOrgIds = organizations.map(org => org.id);
      const available = allOrgs.filter((org: AvailableOrganization) => !memberOrgIds.includes(org.id));
      
      setAvailableOrgs(available);
    } catch (err) {
      console.error('Failed to load available organizations:', err);
      setRequestError('Failed to load organizations. Please try again.');
    }
  };

  const handleRequestAccess = async () => {
    if (!selectedOrgId) {
      setRequestError('Please select an organization');
      return;
    }

    try {
      setSubmitting(true);
      setRequestError('');
      
      await api.post('/membership-requests', {
        organization_id: selectedOrgId,
        message: requestMessage || undefined
      });
      
      setRequestSuccess('Access request submitted successfully! The organization admin will review your request.');
      setSelectedOrgId('');
      setRequestMessage('');
      
      // Close modal after 2 seconds
      setTimeout(() => {
        setShowRequestModal(false);
        setRequestSuccess('');
      }, 2000);
    } catch (err: any) {
      setRequestError(err.response?.data?.error?.message || 'Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const openRequestModal = async () => {
    setIsOpen(false);
    setShowRequestModal(true);
    setRequestError('');
    setRequestSuccess('');
    await fetchAvailableOrganizations();
  };

  const currentOrg = organizations.find(org => org.id === currentOrganizationId);

  return (
    <>
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={switching}
        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-0"
      >
        <Building2 className="w-5 h-5 text-gray-600 dark:text-gray-400 flex-shrink-0" />
        <div className="text-left min-w-0 max-w-[120px] sm:max-w-[180px]">
          <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
            {currentOrg?.name || 'Select Organization'}
          </div>
          {currentOrg && (
            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {currentOrg.role}
            </div>
          )}
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-72 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 max-h-96 overflow-y-auto">
          <div className="p-2">
            <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
              Your Organizations
            </div>
            {loading ? (
              <div className="px-3 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                Loading...
              </div>
            ) : (
              <div className="space-y-1">
                {organizations.map((org) => (
                  <button
                    key={org.id}
                    onClick={() => handleSwitch(org.id)}
                    disabled={switching}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                      org.id === currentOrganizationId
                        ? 'bg-blue-50 dark:bg-blue-900/20'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <div className={`p-2 rounded-lg ${
                      org.id === currentOrganizationId
                        ? 'bg-blue-100 dark:bg-blue-900'
                        : 'bg-gray-100 dark:bg-gray-700'
                    }`}>
                      <Building2 className={`w-4 h-4 ${
                        org.id === currentOrganizationId
                          ? 'text-blue-600 dark:text-blue-400'
                          : 'text-gray-600 dark:text-gray-400'
                      }`} />
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {org.name}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {org.role}
                      </div>
                    </div>
                    {org.id === currentOrganizationId && (
                      <Check className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                    )}
                    {switching && org.id !== currentOrganizationId && (
                      <div className="w-5 h-5 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
                    )}
                  </button>
                ))}
              </div>
            )}
            
            {/* Request Access Button - Only show for non-platform admins */}
            {!isPlatformAdmin && (
              <div className="border-t border-gray-200 dark:border-gray-700 p-2">
                <button
                  onClick={openRequestModal}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm font-medium text-blue-600 dark:text-blue-400"
                >
                  <Plus className="w-4 h-4" />
                  Request Access to Organization
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Request Access Modal */}
      {showRequestModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 overflow-y-auto p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6 my-auto max-h-[calc(100dvh-2rem)] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              Request Organization Access
            </h2>

            {requestError && (
              <Alert type="error" className="mb-4" onClose={() => setRequestError('')}>
                {requestError}
              </Alert>
            )}

            {requestSuccess && (
              <Alert type="success" className="mb-4">
                {requestSuccess}
              </Alert>
            )}

            {!requestSuccess && (
              <>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Select Organization *
                    </label>
                    <select
                      value={selectedOrgId}
                      onChange={(e) => setSelectedOrgId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      disabled={submitting}
                    >
                      <option value="">Choose an organization...</option>
                      {availableOrgs.map((org) => (
                        <option key={org.id} value={org.id}>
                          {org.name}
                        </option>
                      ))}
                    </select>
                    {availableOrgs.length === 0 && (
                      <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                        You are already a member of all available organizations.
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Message (Optional)
                    </label>
                    <textarea
                      value={requestMessage}
                      onChange={(e) => setRequestMessage(e.target.value)}
                      placeholder="Why do you want to join this organization?"
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white resize-none"
                      disabled={submitting}
                    />
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setShowRequestModal(false)}
                    disabled={submitting}
                    fullWidth
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    onClick={handleRequestAccess}
                    disabled={submitting || !selectedOrgId || availableOrgs.length === 0}
                    fullWidth
                  >
                    {submitting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Submit Request
                      </>
                    )}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
    </>
  );
}

// Made with Bob